import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { layersApi, type LayerPermissionEffect, type LayerPermissionKind } from '../../lib/layersApi';
import { decodeWorkspaceId } from '../../lib/tokens';
import type { Group, RolesOverviewMember } from '../../lib/types';
import { isWorkspaceAdminGroup, isWorkspaceAdminMember } from '../../lib/workspaceAdmin';
import { LayerAccessEditor, type AccessRow } from './LayerAccessEditor';

export function LayerAccessModal({
  layerId,
  layerName,
  onClose,
}: {
  layerId: string;
  layerName: string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<AccessRow[]>([]);
  const [members, setMembers] = useState<RolesOverviewMember[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const workspaceId = decodeWorkspaceId();
    Promise.all([layersApi.listLayerPermissions(layerId), api.getRolesOverview()])
      .then(([perms, overview]) => {
        setRows(perms);
        setMembers(overview.members.filter((m) => !isWorkspaceAdminMember(m, workspaceId)));
        setGroups(overview.groups.filter((g) => !isWorkspaceAdminGroup(g, workspaceId)));
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить права доступа.'))
      .finally(() => setLoading(false));
  }, [layerId]);

  const handleAdd = async (
    principalType: 'user' | 'group',
    principalId: string,
    _principalName: string,
    permission: LayerPermissionKind,
    effect: LayerPermissionEffect,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const row = await layersApi.grantLayerPermission(layerId, principalType, principalId, permission, effect);
      setRows((prev) => [...prev, row]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить правило доступа.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (rowId: string) => {
    setError(null);
    try {
      await layersApi.revokeLayerPermission(layerId, rowId);
      setRows((prev) => prev.filter((r) => r.id !== rowId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось убрать правило доступа.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-4">
      <div className="max-h-[85vh] w-full max-w-[640px] overflow-y-auto rounded-[18px] border border-border-default bg-bg-surface p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-text-primary">Права доступа — «{layerName}»</h2>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="shrink-0 text-lg font-semibold text-text-secondary hover:text-text-primary">
            ×
          </button>
        </div>

        {loading ? (
          <p className="mt-4 text-xs text-text-secondary">Загружаем…</p>
        ) : loadError ? (
          <p className="mt-4 text-xs font-medium text-status-danger">{loadError}</p>
        ) : (
          <div className="mt-4">
            <LayerAccessEditor
              rows={rows}
              members={members}
              groups={groups}
              busy={busy}
              error={error}
              onAdd={handleAdd}
              onRemove={handleRemove}
            />
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
